import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import { useOnboarding } from '../hooks/useOnboarding'
import { useAdminGuard } from '../hooks/useAdminGuard'
import { useBranding } from '../lib/branding'
import OnboardingWizard from '../components/onboarding/OnboardingWizard'
import LoginPage from '../pages/auth/LoginPage'
import SuspendedPage from '../pages/auth/SuspendedPage'
import WelcomePage from '../pages/auth/WelcomePage'
import AdminPage from '../pages/admin/AdminPage'
import ReplenishmentPage from '../pages/replenishment/ReplenishmentPage'
import PurchaseReportPage from '../pages/purchase/PurchaseReportPage'
import OrderHistoryPage from '../pages/purchase/OrderHistoryPage'
import SeparationPage from '../pages/separation/SeparationPage'
import SettingsPage from '../pages/settings/SettingsPage'
import SuppliersPage from '../pages/suppliers/SuppliersPage'
import InactivePage from '../pages/inactive/InactivePage'
import EstoqueDashboardPage from '../pages/dashboard/EstoqueDashboardPage'
import MiguelCompradorPage from '../pages/miguel-comprador/MiguelCompradorPage'
import CostOfGoodsPage from '../pages/cost-of-goods/CostOfGoodsPage'
import CadastroRecebimentoPage from '../pages/cadastro-recebimento/CadastroRecebimentoPage'
import PlansPage from '../pages/billing/PlansPage'
import TermsPage from '../pages/legal/TermsPage'
import PrivacyPage from '../pages/legal/PrivacyPage'
import DeletedAccountPage from '../pages/auth/DeletedAccountPage'
import MobileHomePage from '../pages/MobileHome'
import { useIsMobile } from '../hooks/useIsMobile'
import { useMobileV2 } from '../lib/flags'

/** Raiz: no mobile com launcher v2, a entrada é o Início; senão, Painel (como sempre). */
function HomeGate() {
  const isMobile = useIsMobile()
  const v2 = useMobileV2()
  if (isMobile && v2) return <Navigate to="/inicio" replace />
  return <ReplenishmentPage />
}

function PrivateRoute({ children }: { children: React.ReactNode }) {
  const { session, loading } = useAuth()
  const { loading: loadingOnboarding, needsOnboarding, needsPasswordSetup, companyId, companyActive, deletionRequestedAt, refresh, complete, completePasswordSetup } = useOnboarding()
  useBranding(companyId)

  if (loading || (session && loadingOnboarding)) {
    return (
      <div className="min-h-screen bg-paper flex items-center justify-center">
        <div className="w-6 h-6 border-2 border-brand-500 border-t-transparent rounded-full animate-spin" />
      </div>
    )
  }

  if (!session) return <Navigate to="/login" replace />

  if (deletionRequestedAt) return <DeletedAccountPage requestedAt={deletionRequestedAt} onCancel={refresh} />

  if (!companyActive) return <SuspendedPage />

  if (needsPasswordSetup) return <WelcomePage onComplete={completePasswordSetup} />

  return (
    <>
      {children}
      {needsOnboarding && companyId && (
        <OnboardingWizard companyId={companyId} onComplete={complete} />
      )}
    </>
  )
}

function SuperAdminRoute({ children }: { children: React.ReactNode }) {
  const { session, loading } = useAuth()
  const { isSuperAdmin, loading: loadingRole } = useAdminGuard()

  if (loading || loadingRole) {
    return (
      <div className="min-h-screen bg-paper flex items-center justify-center">
        <div className="w-6 h-6 border-2 border-brand-500 border-t-transparent rounded-full animate-spin" />
      </div>
    )
  }

  if (!session) return <Navigate to="/login" replace />
  if (!isSuperAdmin) return <Navigate to="/" replace />
  return <>{children}</>
}

function AdminRoute({ children }: { children: React.ReactNode }) {
  const { session, loading } = useAuth()
  const { isAdmin, loading: loadingRole } = useAdminGuard()
  const { loading: loadingOnboarding, needsOnboarding, needsPasswordSetup, companyId, companyActive, deletionRequestedAt, refresh, complete, completePasswordSetup } = useOnboarding()
  useBranding(companyId)

  if (loading || loadingRole || (session && loadingOnboarding)) {
    return (
      <div className="min-h-screen bg-paper flex items-center justify-center">
        <div className="w-6 h-6 border-2 border-brand-500 border-t-transparent rounded-full animate-spin" />
      </div>
    )
  }

  if (!session) return <Navigate to="/login" replace />
  if (deletionRequestedAt) return <DeletedAccountPage requestedAt={deletionRequestedAt} onCancel={refresh} />
  if (!companyActive) return <SuspendedPage />
  if (needsPasswordSetup) return <WelcomePage onComplete={completePasswordSetup} />
  if (!isAdmin) return <Navigate to="/" replace />
  return (
    <>
      {children}
      {needsOnboarding && companyId && (
        <OnboardingWizard companyId={companyId} onComplete={complete} />
      )}
    </>
  )
}

export default function AppRouter() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/termos" element={<TermsPage />} />
        <Route path="/privacidade" element={<PrivacyPage />} />
        <Route path="/admin" element={<SuperAdminRoute><AdminPage /></SuperAdminRoute>} />
        <Route path="/" element={<PrivateRoute><HomeGate /></PrivateRoute>} />
        <Route path="/painel" element={<PrivateRoute><ReplenishmentPage /></PrivateRoute>} />
        <Route path="/inicio" element={<PrivateRoute><MobileHomePage /></PrivateRoute>} />
        {/* Sugestão consolidada no Miguel (sub-aba "Explorar"). Redireciona links antigos. */}
        <Route path="/sugestao-compras" element={<Navigate to="/miguel-comprador" replace />} />
        <Route path="/relatorio-compras" element={<PrivateRoute><PurchaseReportPage /></PrivateRoute>} />
        <Route path="/historico-pedidos" element={<PrivateRoute><OrderHistoryPage /></PrivateRoute>} />
        <Route path="/abastecimento-filial" element={<PrivateRoute><SeparationPage /></PrivateRoute>} />
        <Route path="/cadastro-recebimento" element={<PrivateRoute><CadastroRecebimentoPage /></PrivateRoute>} />
        <Route path="/fornecedor" element={<PrivateRoute><SuppliersPage /></PrivateRoute>} />
        <Route path="/inativos" element={<PrivateRoute><InactivePage /></PrivateRoute>} />
        <Route path="/estoque-dashboard" element={<PrivateRoute><EstoqueDashboardPage /></PrivateRoute>} />
        <Route path="/miguel-comprador" element={<PrivateRoute><MiguelCompradorPage /></PrivateRoute>} />
        <Route path="/custo-mercadoria" element={<PrivateRoute><CostOfGoodsPage /></PrivateRoute>} />
        <Route path="/configuracoes" element={<AdminRoute><SettingsPage /></AdminRoute>} />
        <Route path="/planos" element={<AdminRoute><PlansPage /></AdminRoute>} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  )
}
